import React from "react";
import HtmlTooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { MdArrowForward } from "react-icons/md";
import { Exercise } from "../../../../../models/Exercise";
import { Ticket } from "../../../../../models/Ticket";
import EAModal from "../../../../shared/Modal/EAModal";
import EAButton from "../../../../shared/Buttons/EAButton";
import EAInfoTile from "../../../../shared/InfoTile/EAInfoTile";
import AddToGoogleCalendar from "../../../../shared/CalendarIntegration/AddToGoogleCalendar";
import EAAlert from "../../../../shared/EAAlert";
import EAChip from "../../../../shared/Chip/EAChip";
import EATooltip from "../../../../shared/EATooltip/EATooltip";
import { pluralGodzin } from "../../../../shared/TimeSlot/utils";
import "./ReservationModal.scss";
import ReservationModalHeader from "./ReservationModalHeader";
import ReservationDetailsGrid from "./ReservationDetailsGrid";
import ChangeSwimmingTermSlots from "./ChangeSwimmingTerm/ChangeSwimmingTermSlots";
import useReservationsModal from "./useReservationsModal";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  exercise: Exercise;
  ticket: Ticket;
  onRefreshReservations?: () => void;
}

const ReservationDetailsModal: React.FC<IProps> = ({ isOpen, onClose, exercise, ticket, onRefreshReservations }) => {
  const { t } = useTranslation();
  const {
    view,
    modalTitle,
    modalSubtitle,
    isChangeFlow,
    resignError,
    changeError,
    selectedLesson,
    isResignSubmitting,
    isChangeSubmitting,
    resignButtonState,
    showResignButton,
    showChangeTermButton,
    tooltipMessage,
    changeTermTooltip,
    swimmingChangeLimitApplies,
    changeTermLimitMessage,
    changeTermRemainingCount,
    currentTermDayLabel,
    currentTermTimeLabel,
    newTermDayLabel,
    newTermTimeLabel,
    changeDeadlineHours,
    exerciseLocation,
    handleClose,
    handleBack,
    handleResignClick,
    handleCancelResignation,
    handleConfirmResignation,
    handleStartChangeTerm,
    handleSelectLesson,
    handleCancelSwap,
    handleConfirmSwap,
  } = useReservationsModal({ isOpen, onClose, exercise, ticket, onRefreshReservations });

  return (
    <EAModal
      isOpen={isOpen}
      title={modalTitle}
      onClose={handleClose}
      onBack={isChangeFlow ? handleBack : undefined}
      hideButtons
      desktopWidth="600px"
    >
      <div className="reservation-modal">
        {view === "resign" && (
          <>
            <h5 style={{ marginBottom: "8px" }}>{t("czy_na_pewno_chcesz_zrezygnowac")}</h5>
            <p className="body-text-small text-color-error" style={{ margin: "0 0 16px" }}>
              {t("rezygnacja_info_brak_zwrotu")}
            </p>
            {resignError && <EAAlert type="error" message={resignError} />}
            <div className="reservation-modal__actions">
              <EAButton
                title={t("tak_rezygnuje")}
                onClick={handleConfirmResignation}
                color="error"
                fullWidth
                loading={isResignSubmitting}
                loadingText={t("rezygnowanie") || "Rezygnowanie..."}
              />
              <EAButton
                title={t("nie")}
                onClick={handleCancelResignation}
                variant="outlined"
                color="error"
                fullWidth
                disabled={isResignSubmitting}
              />
            </div>
          </>
        )}

        {view === "change-slots" && (
          <>
            {modalSubtitle && <span className="reservation-modal__step-label">{modalSubtitle}</span>}
            {changeError && <EAAlert type="error" message={changeError} />}

            <div className="reservation-modal__current-term">
              <EAInfoTile date={new Date(exercise.startTime)} size="small" color="gray" />
              <div className="reservation-modal__current-term-content">
                <span className="body-text-small text-color-light-gray">TWÓJ OBECNY TERMIN</span>
                <b className="body-text-small">
                  {currentTermDayLabel}, {currentTermTimeLabel}
                </b>
              </div>
            </div>

            <div className="reservation-modal__info-banner">
              Pokazujemy wolne terminy w Twojej grupie zajęciowej. Rezerwacja zmieni się dopiero po potwierdzeniu w
              następnym kroku.
            </div>

            <ChangeSwimmingTermSlots exercise={exercise} ticket={ticket} enabled onSelectLesson={handleSelectLesson} />

            {changeDeadlineHours > 0 && (
              <div className="reservation-modal__deadline-note">
                Termin możesz zmieniać do {changeDeadlineHours} {pluralGodzin(changeDeadlineHours)} przed zajęciami.
              </div>
            )}
          </>
        )}

        {view === "change-swap" && selectedLesson && (
          <>
            {modalSubtitle && <span className="reservation-modal__step-label">{modalSubtitle}</span>}
            {changeError && <EAAlert type="error" message={changeError} />}

            <div className="reservation-modal__compare">
              <div className="reservation-modal__compare-col">
                <span className="reservation-modal__compare-label">BYŁO</span>
                <b className="reservation-modal__compare-strike">{currentTermDayLabel}</b>
                <span className="reservation-modal__compare-strike">{currentTermTimeLabel}</span>
                {exerciseLocation && (
                  <span className="reservation-modal__compare-strike">{exerciseLocation}</span>
                )}
              </div>
              <MdArrowForward className="reservation-modal__compare-arrow" aria-hidden />
              <div className="reservation-modal__compare-col">
                <span className="reservation-modal__compare-label">BĘDZIE</span>
                <b>{newTermDayLabel}</b>
                <span>{newTermTimeLabel}</span>
                {selectedLesson.location && <span>{selectedLesson.location}</span>}
              </div>
            </div>

            <p className="body-text-small text-color-dark-gray" style={{ margin: 0 }}>
              Po potwierdzeniu zapiszemy Cię na nowy termin, a stary zwolni się dla innych uczestników.
            </p>

            {changeDeadlineHours > 0 && (
              <div className="reservation-modal__deadline-note">
                Termin możesz zmienić jeszcze raz, najpóźniej {changeDeadlineHours}{" "}
                {pluralGodzin(changeDeadlineHours)} przed zajęciami.
              </div>
            )}

            {resignError && <EAAlert type="error" message={resignError} />}

            <div className="reservation-modal__actions">
              <EAButton
                title="Potwierdź nowy termin"
                onClick={handleConfirmSwap}
                fullWidth
                loading={isChangeSubmitting}
                loadingText={t("zapisywanie") || "Zapisywanie..."}
              />
              <EAButton
                title="Wróć do listy"
                onClick={handleCancelSwap}
                variant="text"
                fullWidth
                disabled={isChangeSubmitting}
              />
            </div>
          </>
        )}

        {view === "details" && (
          <>
            <ReservationModalHeader exercise={exercise} ticket={ticket} />
            <ReservationDetailsGrid exercise={exercise} ticket={ticket} />
            {resignError && <EAAlert type="error" message={resignError} />}
            {swimmingChangeLimitApplies && (showResignButton || showChangeTermButton) && (
              <EATooltip content={changeTermLimitMessage}>
                {changeTermRemainingCount > 0 ? (
                  <EAChip label="Pozostałe zmiany terminu" variant="default" badge={changeTermRemainingCount} />
                ) : (
                  <EAChip label="Limit zmian terminu wykorzystany" variant="warning" />
                )}
              </EATooltip>
            )}
            <div className="reservation-modal__actions">
              {showChangeTermButton && (
                <HtmlTooltip
                  placement="top"
                  arrow
                  title={<article dangerouslySetInnerHTML={{ __html: changeTermTooltip }} />}
                >
                  <span style={{ flex: 1 }}>
                    <EAButton
                      title={t("zmien_termin")}
                      variant="outlined"
                      onClick={handleStartChangeTerm}
                      disabled={resignButtonState.disabled}
                      fullWidth
                    />
                  </span>
                </HtmlTooltip>
              )}
              {showResignButton && !showChangeTermButton && (
                <HtmlTooltip
                  placement="top"
                  arrow
                  title={<article dangerouslySetInnerHTML={{ __html: tooltipMessage }} />}
                >
                  <span style={{ flex: 1 }}>
                    <EAButton
                      title={t("zrezygnuj")}
                      onClick={handleResignClick}
                      color="error"
                      disabled={resignButtonState.disabled}
                      fullWidth
                    />
                  </span>
                </HtmlTooltip>
              )}
              <AddToGoogleCalendar exercise={exercise} variant="filled" color="primary" fullWidth noMargins={false} />
            </div>
          </>
        )}
      </div>
    </EAModal>
  );
};

export default ReservationDetailsModal;
